import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef } from "react";
import { type UseFormRegisterReturn, useForm } from "react-hook-form";
import { z } from "zod";
import { useCreateBusiness, useOnboardingStatus } from "@/api/hooks";
import type { OnboardingStatus } from "@/api/types";
import { FieldLabel } from "@/components/forms/field-label";
import { FormError } from "@/components/forms/form-error";
import { StepHeader } from "@/components/forms/step-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { persistBusinessId } from "@/features/onboarding/use-business-id";
import { extractApiError } from "@/lib/extract-api-error";
import { handleFormError } from "@/lib/handle-form-error";
import { sessionRevision } from "@/lib/session-request-scope";

const businessSchema = z.object({
  country: z.string().min(1, "Country is required"),
  name: z.string().min(1, "Business name is required"),
  website: z.string().url("Enter a valid URL").optional().or(z.literal("")),
});

type BusinessValues = z.infer<typeof businessSchema>;

const REGISTERED_FIELDS = new Set<keyof BusinessValues>(["name", "website", "country"]);

function BusinessStep() {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const status = useOnboardingStatus();
  const nameErrorId = useId();
  const websiteErrorId = useId();
  const navigate = useNavigate();
  const createBusiness = useCreateBusiness();
  const revision = sessionRevision();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<BusinessValues>({
    resolver: zodResolver(businessSchema),
    defaultValues: { name: "", website: "", country: "" },
  });

  const onSubmit = handleSubmit(async (data) => {
    if (
      sessionRevision() !== revision ||
      createBusiness.isPending ||
      !status.data ||
      status.isError
    )
      return;
    try {
      const result = await createBusiness.mutateAsync({
        country: data.country,
        name: data.name,
        website: data.website || undefined,
      });
      if (!mounted.current || sessionRevision() !== revision) return;
      persistBusinessId(result.id);
      void navigate({ to: "/onboarding/services" });
    } catch (error) {
      if (!mounted.current || sessionRevision() !== revision) return;
      handleFormError<BusinessValues>(error, setError, undefined, REGISTERED_FIELDS);
    }
  });

  return (
    <div className="space-y-6">
      <StepHeader
        title="Tell us about your business"
        description="What's the name of your business? You can always change this later."
      />

      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={errors.root?.message} />

        <div className="space-y-1">
          <FieldLabel
            htmlFor={`${nameErrorId}-input`}
            required
            className="text-[13px] font-medium text-ink"
          >
            Business name
          </FieldLabel>
          <Input
            id={`${nameErrorId}-input`}
            aria-required="true"
            aria-label="Business name"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? nameErrorId : undefined}
            placeholder="Business name"
            autoComplete="organization"
            error={!!errors.name}
            disabled={createBusiness.isPending}
            {...register("name")}
          />
          {errors.name && (
            <Text variant="small" className="text-danger">
              <span id={nameErrorId}>{errors.name.message}</span>
            </Text>
          )}
        </div>

        <div className="space-y-1">
          <FieldLabel
            htmlFor={`${websiteErrorId}-input`}
            className="text-[13px] font-medium text-ink"
          >
            Website
          </FieldLabel>
          <Input
            id={`${websiteErrorId}-input`}
            aria-label="Website"
            aria-invalid={!!errors.website}
            aria-describedby={errors.website ? websiteErrorId : undefined}
            placeholder="Website"
            type="url"
            autoComplete="url"
            error={!!errors.website}
            disabled={createBusiness.isPending}
            {...register("website")}
          />
          {errors.website && (
            <Text variant="small" className="text-danger">
              <span id={websiteErrorId}>{errors.website.message}</span>
            </Text>
          )}
        </div>

        <BusinessCountryField
          options={status.data?.countries}
          error={errors.country?.message}
          disabled={status.isLoading || status.isError || createBusiness.isPending}
          field={register("country")}
        />
        {status.isError && (
          <div className="space-y-3">
            <FormError
              message={extractApiError(status.error, "Unable to load countries. Please try again.")}
            />
            <Button type="button" variant="secondary" onClick={() => void status.refetch()}>
              Try again
            </Button>
          </div>
        )}
        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={createBusiness.isPending}
          disabled={!status.data || status.isError}
        >
          Continue
        </Button>
      </form>
    </div>
  );
}

function BusinessCountryField({
  options,
  error,
  disabled,
  field,
}: {
  options: OnboardingStatus["countries"] | undefined;
  error?: string;
  disabled: boolean;
  field: UseFormRegisterReturn<"country">;
}) {
  const countryId = useId();
  return (
    <div className="space-y-1">
      <FieldLabel htmlFor={countryId} required className="text-[13px] font-medium text-ink">
        Country
      </FieldLabel>
      <select
        id={countryId}
        aria-required="true"
        aria-invalid={!!error}
        aria-describedby={error ? `${countryId}-error` : undefined}
        disabled={disabled}
        className="h-14 w-full rounded-sm border-[1.5px] border-fog bg-surface px-3.5 text-[16px] text-ink focus:border-electric focus:outline-none focus:ring-2 focus:ring-electric-50"
        {...field}
      >
        <option value="">Select country</option>
        {options?.map((country) => (
          <option key={country.value} value={country.value}>
            {country.label}
          </option>
        ))}
      </select>
      {error && (
        <Text variant="small" className="text-danger">
          <span id={`${countryId}-error`}>{error}</span>
        </Text>
      )}
    </div>
  );
}

export { BusinessStep };
